import * as z from 'zod';
import { userSchema, userRoleSchema, type UserRole } from '@/entities/user';

/** Translator shape (i18next's `t`), passed in so validation messages localize. */
type Translate = (key: string) => string;

// Modal payloads, validated at the modal registry boundary. The `mode`
// discriminant lets one modal component serve both create and edit; the edit
// payload carries the row being edited so the form can seed its defaults while
// the by-id read is in flight.

export const addUserPayloadSchema = z.object({
  mode: z.literal('add'),
  initialRole: userRoleSchema.exclude(['merchant-admin', 'unknown']).optional(),
  onCreated: z
    .custom<
      (role: Extract<UserRole, 'manager' | 'cashier'>) => void
    >((value) => typeof value === 'function', { message: 'onCreated must be a function' })
    .optional(),
});
export type AddUserPayload = z.infer<typeof addUserPayloadSchema>;

export const editUserPayloadSchema = z.object({
  mode: z.literal('edit'),
  user: userSchema,
});
export type EditUserPayload = z.infer<typeof editUserPayloadSchema>;

/**
 * The CREATE form — an attach-by-phone, so it collects everything the identity
 * needs: names, phone and role.
 *
 * No email and no password field, though the Figma draws both. `v1Employee`
 * has no email at all, and the password belongs to auth-service, which mints
 * it during the attach — neither has anywhere to go in the request.
 */
export function buildCreateUserFormSchema(t: Translate) {
  return z.object({
    firstName: z.string().trim().min(1, t('users.form.firstNameRequired')),
    lastName: z.string().trim().min(1, t('users.form.lastNameRequired')),
    // `FormField.Phone` stores the canonical E.164 string — `+998` plus the
    // 9-digit national number, so a COMPLETE number is exactly 13 characters.
    // The old `min(9)` accepted `+998 90 1`: a half-typed number that passed
    // client validation and was then rejected by auth-service, which the form
    // reported as an unexplained failure to add the employee.
    phone: z
      .string()
      .trim()
      .regex(/^\+998\d{9}$/, t('users.form.phoneRequired')),
    // The two roles the merchant contour may assign. `merchant-admin` is
    // excluded because the backend refuses it from this context, and `unknown`
    // because it is a local read-side fallback, never something to send.
    role: userRoleSchema.exclude(['merchant-admin', 'unknown'], {
      message: t('users.form.roleRequired'),
    }),
  });
}
export type CreateUserFormValues = z.infer<ReturnType<typeof buildCreateUserFormSchema>>;

/**
 * The EDIT form — names only.
 *
 * `PATCH /merchant-employees/{id}` accepts `first_name`/`last_name` and nothing
 * else; phone and role live in auth-service and are not editable from the
 * merchant panel. The form renders them as read-only context rather than
 * offering inputs whose changes the request would silently drop.
 */
export function buildEditUserFormSchema(t: Translate) {
  return z.object({
    firstName: z.string().trim().min(1, t('users.form.firstNameRequired')),
    lastName: z.string().trim().min(1, t('users.form.lastNameRequired')),
    locationIds: z.array(z.string()),
  });
}
export type EditUserFormValues = z.infer<ReturnType<typeof buildEditUserFormSchema>>;
